import { Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { map } from 'es-toolkit/compat';
import React from 'react';

// Shared anatomy of an export form's "Scope" and "Export options" sections. A choice with several options is a
// radio group (or a checkbox group); a choice with exactly one is read-only text, never a lone radio or checkbox.

export interface ExportScopeOption {
  value: string;
  label: string;
  disabled?: boolean;
  // Why the option is unavailable, or a short explanation of what it means.
  note?: string;
}

const FieldHeading: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <Typography variant="subtitle2" id={id}>
    {children}
  </Typography>
);

const NoteText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
    {children}
  </Typography>
);

export const ExportScopeFields: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack spacing={1.5}>{children}</Stack>
);

export const ExportScopeValue: React.FC<{ label: string; note?: string; children: React.ReactNode }> = ({
  label,
  note,
  children,
}) => {
  const headingId = React.useId();
  return (
    <div role="group" aria-labelledby={headingId}>
      <FieldHeading id={headingId}>{label}</FieldHeading>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
      {note && <NoteText>{note}</NoteText>}
    </div>
  );
};

interface ExportScopeFieldProps {
  id: string;
  label: string;
  options: ExportScopeOption[];
  value: string;
  onChange: (value: string) => void;
  // Rendered after the options, for example a picker that belongs to the selected option.
  children?: React.ReactNode;
}

export const ExportScopeField: React.FC<ExportScopeFieldProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  children,
}) => {
  const headingId = `${id}-label`;
  if (options.length === 1) {
    const [only] = options;
    return (
      <div role="group" aria-labelledby={headingId}>
        <FieldHeading id={headingId}>{label}</FieldHeading>
        <Typography variant="body2" component="div">
          {only.label}
        </Typography>
        {only.note && <NoteText>{only.note}</NoteText>}
        {children}
      </div>
    );
  }
  return (
    <div>
      <FieldHeading id={headingId}>{label}</FieldHeading>
      <RadioGroup aria-labelledby={headingId} value={value} onChange={(event) => onChange(event.target.value)}>
        {map(options, (option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            control={<Radio />}
            label={
              <>
                {option.label}
                {option.note && <NoteText>{option.note}</NoteText>}
              </>
            }
          />
        ))}
      </RadioGroup>
      {children}
    </div>
  );
};

export interface ExportOutputChoice {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  note?: string;
}

interface ExportOutputChoicesProps {
  label: string;
  outputs: ExportOutputChoice[];
  onChange: (id: string, checked: boolean) => void;
}

export const ExportOutputChoices: React.FC<ExportOutputChoicesProps> = ({ label, outputs, onChange }) => {
  if (outputs.length === 1) {
    const [only] = outputs;
    return (
      <div role="group" aria-label={label}>
        <Typography variant="body2" component="div">
          {only.label}
        </Typography>
        {only.note && <NoteText>{only.note}</NoteText>}
      </div>
    );
  }
  return (
    <FormGroup role="group" aria-label={label}>
      {map(outputs, (output) => (
        <FormControlLabel
          key={output.id}
          disabled={output.disabled}
          control={
            <Checkbox checked={output.checked} onChange={(event) => onChange(output.id, event.target.checked)} />
          }
          label={
            <>
              {output.label}
              {output.note && <NoteText>{output.note}</NoteText>}
            </>
          }
        />
      ))}
    </FormGroup>
  );
};
